import { getAllClientCampaignBookings, ClientCampaignBooking } from '@round/api';
import { useCallback, useState } from 'react';
import useAbortableEffect from '../../../../../hooks/useAbortableEffect';

export type UseCampaignBookingsResult = {
    isLoading: boolean;
    hasErrorLoading: boolean;
    bookings: ClientCampaignBooking[];
    isInitialized: boolean;
};

export function useCampaignBookings(campaignId: number | null): UseCampaignBookingsResult {
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoading, setHasErrorLoading] = useState(false);
    const [bookings, setBookings] = useState<ClientCampaignBooking[]>([]);

    const fetchBookings = useCallback(async (campaignId: number, requestInit?: RequestInit) => {
        try {
            setHasErrorLoading(false);
            setIsLoading(true);
            const bookings = await getAllClientCampaignBookings(campaignId, requestInit);
            setBookings(bookings);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }

            setHasErrorLoading(true);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            if (typeof campaignId === 'number') {
                fetchBookings(campaignId, { signal }).then(() => setIsInitialized(true));
            }
        },
        [campaignId, fetchBookings]
    );

    return {
        bookings,
        hasErrorLoading,
        isLoading,
        isInitialized,
    };
}
